import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "wouter";
import Layout from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useClaims, CLAIMS_PAGE_SIZE, useClaimAssigneeOptions, useSharedClaimFilterOptions } from "@/hooks/use-claims";
import { useStatuses } from "@/hooks/use-statuses";
import { useClaimPipelines } from "@/hooks/use-claim-pipelines";
import { useFolders } from "@/hooks/use-folders";
import { useAuth } from "@/hooks/use-auth";
import { ClaimsToolbar } from "@/components/claims/ClaimsToolbar";
import { ClaimsList } from "@/components/claims/ClaimsList";
import { ClaimsTable } from "@/components/claims/ClaimsTable";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { Claim } from "@/types/claims";
import { LayoutList, Table as TableIcon, ChevronLeft, ChevronRight } from "lucide-react";

function idFilter(value: string | null): string {
    return value && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value)) ? value : "all";
}

export default function Claims() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<"list" | "table">("list");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const statusFilter = idFilter(searchParams.get("claimStatusTemplateId"));
    const pipelineFilter = idFilter(searchParams.get("pipelineId"));
    const [assigneeFilter, setAssigneeFilter] = useState("all");
    const [showArchivedOnly, setShowArchivedOnly] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState<number | "all">("all");
    const [claimToDelete, setClaimToDelete] = useState<Claim | null>(null);

    // Debounce search to avoid a request on every keystroke
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleSearch = (value: string) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setPage(1);
        }, 300);
    };

    // Clear pending debounce on unmount
    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const handleFilterChange = (value: string) => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            if (value === "all") next.delete("claimStatusTemplateId");
            else next.set("claimStatusTemplateId", value);
            return next;
        }, { replace: true });
        setPage(1);
    };

    const handlePipelineChange = (value: string) => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            if (value === "all") next.delete("pipelineId");
            else next.set("pipelineId", value);
            next.delete("claimStatusTemplateId");
            return next;
        }, { replace: true });
        setPage(1);
    };

    const handleFolderSelect = (id: number | "all") => {
        setSelectedFolderId(id);
        setPage(1);
    };

    const claimStatusTemplateId = statusFilter !== "all" ? parseInt(statusFilter) : undefined;
    const pipelineId = pipelineFilter !== "all" ? parseInt(pipelineFilter) : undefined;
    const assigneeUserId = assigneeFilter !== "all" ? parseInt(assigneeFilter) : undefined;
    const claimFolderId = selectedFolderId !== "all" ? selectedFolderId : undefined;
    const archivedOnly = showArchivedOnly;

    useEffect(() => {
        setPage(1);
    }, [statusFilter, pipelineFilter]);

    const { claims, total, totalPages, isLoading: isLoadingClaims, isPlaceholderData, updateStatus, updatePipeline, updateFolder, setArchived, deleteClaimAsync, isArchiving } = useClaims({
        page,
        search: debouncedSearch || undefined,
        claimStatusTemplateId,
        pipelineId,
        claimFolderId,
        assigneeUserId,
        archivedOnly,
    });
    const { statuses, isLoading: isLoadingStatuses } = useStatuses();
    const { pipelines: sharedPipelines, statuses: sharedStatuses, isLoading: isLoadingSharedFilters } = useSharedClaimFilterOptions(archivedOnly);
    // Include inactive workflows so claims that still reference a deactivated
    // workflow remain labeled and filterable until they are moved.
    const { pipelines, isLoading: isLoadingPipelines } = useClaimPipelines({ includeInactive: true });
    const assignablePipelines = pipelines.filter((pipeline) => pipeline.isActive);
    const { folders, manageableFolders, isLoading: isLoadingFolders } = useFolders();
    const { assigneeOptions, isLoading: isLoadingAssignees } = useClaimAssigneeOptions();

    // Include folder loading so the list/table render their final layout once,
    // avoiding a flicker/re-layout when folder controls appear after claims load.
    const isLoading = isLoadingClaims || isLoadingStatuses || isLoadingFolders || isLoadingPipelines || isLoadingSharedFilters;

    // Clamp page when total shrinks (e.g. deletion reduces page count)
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    // Reset the folder filter if the selected folder no longer exists
    // (e.g. it was deleted, including deleting the last remaining folder).
    useEffect(() => {
        if (
            selectedFolderId !== "all" &&
            !isLoadingFolders &&
            !folders.some((f) => f.id === selectedFolderId)
        ) {
            setSelectedFolderId("all");
        }
    }, [folders, isLoadingFolders, selectedFolderId]);

    // Reset the assignee filter if the selected user is no longer assigned to any
    // accessible claim (e.g. their last assignment was revoked).
    useEffect(() => {
        if (
            assigneeFilter !== "all" &&
            !isLoadingAssignees &&
            !assigneeOptions.some((u) => String(u.id) === assigneeFilter)
        ) {
            setAssigneeFilter("all");
            setPage(1);
        }
    }, [assigneeOptions, isLoadingAssignees, assigneeFilter]);


    return (
        <Layout>
            <div className="space-y-6 py-4 sm:py-8 md:px-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <PageHeader
                        title="Claims Management"
                        description="View and manage all your insurance claims"
                    />
                    <div className="flex w-full flex-row items-center gap-3 sm:w-auto sm:justify-end">
                        <div className="flex shrink-0 items-center gap-2 rounded-md border bg-muted px-3 py-2">
                            <Switch
                                id="claims-archived-only"
                                checked={showArchivedOnly}
                                onCheckedChange={(value) => {
                                    setShowArchivedOnly(value);
                                    setPage(1);
                                }}
                            />
                            <Label htmlFor="claims-archived-only" className="text-sm">Archived</Label>
                        </div>
                        <div className="flex flex-1 bg-muted p-1 rounded-lg border sm:flex-initial">
                            <Button
                                variant={viewMode === "list" ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode("list")}
                                className={viewMode === "list" ? "bg-card shadow-sm hover:bg-card flex-1 sm:flex-initial" : "flex-1 sm:flex-initial"}
                            >
                                <LayoutList className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">List</span>
                            </Button>
                            <Button
                                variant={viewMode === "table" ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode("table")}
                                className={viewMode === "table" ? "bg-card shadow-sm hover:bg-card flex-1 sm:flex-initial" : "flex-1 sm:flex-initial"}
                            >
                                <TableIcon className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">Table</span>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <ClaimsToolbar
                        onSearch={handleSearch}
                        onFilterChange={handleFilterChange}
                        statusFilter={statusFilter}
                        pipelineFilter={pipelineFilter}
                        onPipelineChange={handlePipelineChange}
                        pipelines={[...pipelines, ...sharedPipelines]}
                        statuses={[...statuses, ...sharedStatuses]}
                        assigneeFilter={assigneeFilter}
                        onAssigneeChange={(value) => { setAssigneeFilter(value); setPage(1); }}
                        assigneeOptions={assigneeOptions}
                        selectedFolderId={selectedFolderId}
                        onFolderSelect={handleFolderSelect}
                    />

                    {viewMode === "list" ? (
                        <ClaimsList
                            claims={claims}
                            statuses={statuses}
                            pipelines={pipelines}
                            assignablePipelines={assignablePipelines}
                            viewerCompanyId={user?.companyId ?? null}
                            folders={folders}
                            assignableFolders={manageableFolders}
                            onStatusChange={(id, statusTemplateId) => updateStatus({ claimId: id, statusTemplateId })}
                            onPipelineChange={(id, pipelineId) => updatePipeline({ claimId: id, pipelineId })}
                            onFolderChange={(id, claimFolderId) => updateFolder({ claimId: id, claimFolderId })}
                            onArchiveToggle={(id, isArchived) => setArchived({ claimId: id, isArchived })}
                            onDeleteClick={(id) => setClaimToDelete(claims.find((c) => c.id === id) ?? null)}
                            isArchiving={isArchiving}
                            isLoading={isLoading}
                        />
                    ) : (
                        <ClaimsTable
                            claims={claims}
                            statuses={statuses}
                            pipelines={pipelines}
                            assignablePipelines={assignablePipelines}
                            viewerCompanyId={user?.companyId ?? null}
                            folders={folders}
                            assignableFolders={manageableFolders}
                            onStatusChange={(id, statusTemplateId) => updateStatus({ claimId: id, statusTemplateId })}
                            onPipelineChange={(id, pipelineId) => updatePipeline({ claimId: id, pipelineId })}
                            onFolderChange={(id, claimFolderId) => updateFolder({ claimId: id, claimFolderId })}
                            onArchiveToggle={(id, isArchived) => setArchived({ claimId: id, isArchived })}
                            onDeleteClick={(id) => setClaimToDelete(claims.find((c) => c.id === id) ?? null)}
                            isArchiving={isArchiving}
                            isLoading={isLoading}
                        />
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-center text-sm text-muted-foreground sm:text-left">
                                Showing {(page - 1) * CLAIMS_PAGE_SIZE + 1}–{Math.min(page * CLAIMS_PAGE_SIZE, total)} of {total} claims
                            </p>
                            <div className="flex items-center justify-between gap-2 sm:justify-end">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1 || isPlaceholderData}
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Previous
                                </Button>
                                <span className="whitespace-nowrap px-2 text-sm text-muted-foreground">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages || isPlaceholderData}
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDeleteDialog
                open={claimToDelete !== null}
                onOpenChange={(open) => { if (!open) setClaimToDelete(null); }}
                onConfirm={async () => { if (claimToDelete) await deleteClaimAsync(claimToDelete.id); }}
                title="Delete Claim"
                description={
                    claimToDelete
                        ? `Are you sure you want to delete claim ${claimToDelete.claimNumber}? This permanently removes the claim and all of its data and cannot be undone.`
                        : ""
                }
            />
        </Layout>
    );
}
